
import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';

interface CaseStudy {
  id: string;
  title: string;
  client: string;
  category: string;
  location: string;
  flag: string;
  year: string;
  duration: string;
  heroImage: string;
  thumbnail: string;
  challenge: string;
  solution: string;
  results: { metric: string; label: string }[];
  services: string[];
  testimonial: { quote: string; author: string; role: string };
  gallery: string[];
}

interface CaseStudyDetailProps {
  project: CaseStudy;
  onBack: () => void;
}

export default function CaseStudyDetail({ project, onBack }: CaseStudyDetailProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [activeGalleryImg, setActiveGalleryImg] = useState(0);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsLoaded(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [project.id]);

  return (
    <div ref={contentRef} className="min-h-screen bg-white">
      {/* Hero */}
      <section className="relative h-[50vh] sm:h-[60vh] lg:h-[70vh] overflow-hidden">
        <img
          src={project.heroImage}
          alt={project.title}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0D0D0D] via-[#0D0D0D]/50 to-[#0D0D0D]/30"></div>

        <div className={`absolute bottom-0 left-0 right-0 p-6 sm:p-10 lg:p-16 transition-all duration-700 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="max-w-7xl mx-auto">
            <button
              onClick={onBack}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/10 rounded-full text-white text-xs font-semibold mb-5 hover:bg-white/20 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line text-sm"></i>
              Back to Portfolio
            </button>

            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span className="px-3 py-1 bg-[#038C7F] text-white text-[10px] sm:text-xs font-bold rounded-full uppercase tracking-wider">{project.category}</span>
              <span className="text-base">{project.flag}</span>
              <span className="text-xs text-white/70 font-medium">{project.location}</span>
              <span className="w-1 h-1 bg-white/30 rounded-full"></span>
              <span className="text-xs text-white/50">{project.year}</span>
              <span className="w-1 h-1 bg-white/30 rounded-full"></span>
              <span className="text-xs text-white/50">{project.duration}</span>
            </div>

            <h1 className="text-2xl sm:text-3xl lg:text-5xl xl:text-6xl font-black text-white leading-tight max-w-4xl">
              {project.title}
            </h1>
          </div>
        </div>
      </section>

      {/* Results Bar */}
      <section className="bg-[#0D0D0D] border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {project.results.map((result, idx) => (
              <div key={idx} className="text-center lg:text-left">
                <div className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-1">
                  {result.metric}
                </div>
                <div className="text-[10px] sm:text-xs text-gray-500 uppercase tracking-wider font-medium">
                  {result.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 sm:py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-10 lg:gap-16">
            {/* Left – Main Content */}
            <div className="lg:col-span-2">
              {/* Challenge */}
              <div className="mb-10 sm:mb-14">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-[#F2780C]/10 rounded-xl flex items-center justify-center">
                    <i className="ri-error-warning-line text-lg text-[#F2780C]"></i>
                  </div>
                  <h2 className="text-xl sm:text-2xl font-black text-[#0D0D0D]">The Challenge</h2>
                </div>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed">{project.challenge}</p>
              </div>

              {/* Solution */}
              <div className="mb-10 sm:mb-14">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-[#038C7F]/10 rounded-xl flex items-center justify-center">
                    <i className="ri-lightbulb-line text-lg text-[#038C7F]"></i>
                  </div>
                  <h2 className="text-xl sm:text-2xl font-black text-[#0D0D0D]">Our Solution</h2>
                </div>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed">{project.solution}</p>
              </div>

              {/* Gallery */}
              <div className="mb-10 sm:mb-14">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 bg-[#038C7F]/10 rounded-xl flex items-center justify-center">
                    <i className="ri-image-line text-lg text-[#038C7F]"></i>
                  </div>
                  <h2 className="text-xl sm:text-2xl font-black text-[#0D0D0D]">Project Gallery</h2>
                </div>

                <div className="relative w-full h-[250px] sm:h-[350px] lg:h-[420px] rounded-2xl overflow-hidden mb-3">
                  <img
                    src={project.gallery[activeGalleryImg]}
                    alt={`${project.title} gallery ${activeGalleryImg + 1}`}
                    className="w-full h-full object-cover object-top transition-all duration-500"
                  />
                  <div className="absolute bottom-3 right-3 px-3 py-1.5 bg-black/60 backdrop-blur-sm rounded-full text-[10px] text-white font-medium">
                    {activeGalleryImg + 1} / {project.gallery.length}
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-2 sm:gap-3">
                  {project.gallery.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => setActiveGalleryImg(idx)}
                      className={`relative h-[70px] sm:h-[90px] rounded-lg overflow-hidden cursor-pointer transition-all duration-300 ${
                        activeGalleryImg === idx ? 'ring-2 ring-[#038C7F] ring-offset-2' : 'opacity-60 hover:opacity-100'
                      }`}
                    >
                      <img src={img} alt={`Thumbnail ${idx + 1}`} className="w-full h-full object-cover object-top" />
                    </button>
                  ))}
                </div>
              </div>

              {/* Testimonial */}
              <div className="bg-gradient-to-br from-[#0D0D0D] to-[#1a1a1a] rounded-2xl p-6 sm:p-8 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-[#038C7F]/10 rounded-full blur-2xl"></div>
                <i className="ri-double-quotes-l text-4xl sm:text-5xl text-[#038C7F]/20 block mb-4"></i>
                <p className="text-base sm:text-lg text-white/90 font-medium leading-relaxed mb-5 relative">
                  {project.testimonial.quote}
                </p>
                <div className="relative">
                  <div className="text-sm font-bold text-white">{project.testimonial.author}</div>
                  <div className="text-xs text-gray-500">{project.testimonial.role}, {project.client}</div>
                </div>
              </div>
            </div>

            {/* Right – Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-28 space-y-6">
                {/* Project Info Card */}
                <div className="bg-gray-50 rounded-2xl p-5 sm:p-6">
                  <h3 className="text-sm font-bold text-[#0D0D0D] mb-4 flex items-center gap-2">
                    <i className="ri-information-line text-[#038C7F]"></i>
                    Project Details
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500 font-medium">Client</span>
                      <span className="text-xs font-bold text-[#0D0D0D]">{project.client}</span>
                    </div>
                    <div className="h-px bg-gray-200"></div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500 font-medium">Category</span>
                      <span className="text-xs font-bold text-[#0D0D0D]">{project.category}</span>
                    </div>
                    <div className="h-px bg-gray-200"></div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500 font-medium">Location</span>
                      <span className="text-xs font-bold text-[#0D0D0D]">{project.flag} {project.location}</span>
                    </div>
                    <div className="h-px bg-gray-200"></div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500 font-medium">Year</span>
                      <span className="text-xs font-bold text-[#0D0D0D]">{project.year}</span>
                    </div>
                    <div className="h-px bg-gray-200"></div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500 font-medium">Duration</span>
                      <span className="text-xs font-bold text-[#0D0D0D]">{project.duration}</span>
                    </div>
                  </div>
                </div>

                {/* Services Used */}
                <div className="bg-gray-50 rounded-2xl p-5 sm:p-6">
                  <h3 className="text-sm font-bold text-[#0D0D0D] mb-4 flex items-center gap-2">
                    <i className="ri-service-line text-[#F2780C]"></i>
                    Services Delivered
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {project.services.map((service, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1.5 bg-white border border-gray-200 text-xs font-medium text-gray-600 rounded-lg"
                      >
                        {service}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Results Summary */}
                <div className="bg-[#038C7F]/5 border border-[#038C7F]/10 rounded-2xl p-5 sm:p-6">
                  <h3 className="text-sm font-bold text-[#0D0D0D] mb-4 flex items-center gap-2">
                    <i className="ri-bar-chart-box-line text-[#038C7F]"></i>
                    Key Results
                  </h3>
                  <div className="space-y-3">
                    {project.results.map((result, idx) => (
                      <div key={idx} className="flex items-center gap-3">
                        <div className="w-12 text-right">
                          <span className="text-lg font-black text-[#038C7F]">{result.metric}</span>
                        </div>
                        <div className="flex-1 text-xs text-gray-600 font-medium">{result.label}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* CTA */}
                <div className="bg-[#0D0D0D] rounded-2xl p-5 sm:p-6 text-center">
                  <h3 className="text-base font-bold text-white mb-2">Want similar results?</h3>
                  <p className="text-xs text-gray-400 mb-4">Let&apos;s discuss how we can help your brand grow.</p>
                  <Link
                    to="/#contact"
                    className="group w-full inline-flex items-center justify-center gap-2 px-5 py-3 bg-[#038C7F] text-white text-sm font-bold rounded-xl hover:bg-[#F2780C] transition-all duration-300 whitespace-nowrap cursor-pointer"
                  >
                    <span>Start a Project</span>
                    <i className="ri-arrow-right-line text-base group-hover:translate-x-1 transition-transform"></i>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
